import type { Attachment, CreateUploadRequest, CreateUploadResponse, PublicConfig } from '@medicail/shared';
import { apiFetch } from './http';
import { getAuthToken } from './authToken';

export type UploadLimits = PublicConfig['uploads'];

const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
  csv: 'text/csv',
};

/** Browsers sometimes report an empty or generic type (md, csv); fall back to the extension. */
export function resolveMimeType(file: Pick<File, 'name' | 'type'>): string {
  const declared = (file.type || '').toLowerCase().split(';')[0]?.trim() ?? '';
  if (declared && declared !== 'application/octet-stream') return declared;
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  return EXT_MIME[ext] ?? declared;
}

export function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

export function describeAccepted(types: readonly string[]): string {
  const parts: string[] = [];
  if (types.some(isImageMime)) parts.push('images');
  if (types.includes('application/pdf')) parts.push('PDFs');
  if (types.some((t) => t.startsWith('text/'))) parts.push('text files');
  if (parts.length === 0) return 'supported files';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export interface FileLike {
  name: string;
  type: string;
  size: number;
}

export interface ValidationResult<T extends FileLike> {
  accepted: T[];
  errors: string[];
}

/**
 * Validate files against server limits. `existingCount` is the number of attachments already in the composer.
 * Returns the files that can be uploaded plus friendly error messages for the rest.
 */
export function validateFiles<T extends FileLike>(
  files: readonly T[],
  existingCount: number,
  limits: UploadLimits,
): ValidationResult<T> {
  const accepted: T[] = [];
  const errors: string[] = [];
  const slots = Math.max(0, limits.maxFilesPerMessage - existingCount);
  let overflow = 0;

  for (const file of files) {
    const mime = resolveMimeType(file);
    if (!limits.acceptedMimeTypes.includes(mime)) {
      errors.push(`"${file.name}" is not a supported file type. You can attach ${describeAccepted(limits.acceptedMimeTypes)}.`);
      continue;
    }
    if (file.size <= 0) {
      errors.push(`"${file.name}" is empty.`);
      continue;
    }
    if (file.size > limits.maxBytes) {
      errors.push(`"${file.name}" is ${formatBytes(file.size)}. Files can be up to ${formatBytes(limits.maxBytes)}.`);
      continue;
    }
    if (accepted.length >= slots) {
      overflow += 1;
      continue;
    }
    accepted.push(file);
  }
  if (overflow > 0) {
    errors.push(`You can attach up to ${limits.maxFilesPerMessage} files per message. ${overflow} file${overflow === 1 ? ' was' : 's were'} not added.`);
  }
  return { accepted, errors };
}

export interface UploadedFile {
  attachmentId: string;
}

/** Create an upload slot, then PUT the bytes (XHR for progress). Resolves with the attachment id. */
export async function uploadFile(
  file: File,
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<UploadedFile> {
  const mimeType = resolveMimeType(file) as CreateUploadRequest['mimeType'];
  const slot = await apiFetch<CreateUploadResponse>('/uploads', {
    method: 'POST',
    json: { fileName: file.name, mimeType, sizeBytes: file.size } satisfies CreateUploadRequest,
    signal: opts.signal,
  });
  await putBytes(slot.uploadUrl, file, mimeType, opts);
  return { attachmentId: slot.attachmentId };
}

/** The dev blob endpoint lives on our own API and needs the bearer token. Presigned S3 URLs must not get it. */
export function needsAuthHeader(url: string): boolean {
  return /\/api\/uploads\/[^/?#]+\/blob(?:[?#]|$)/.test(url);
}

async function putBytes(
  url: string,
  file: File,
  mimeType: string,
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<void> {
  const token = needsAuthHeader(url) ? await getAuthToken() : null;
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mimeType);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(1);
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status}). Please try again.`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    const abort = () => xhr.abort();
    if (opts.signal) {
      if (opts.signal.aborted) return abort();
      opts.signal.addEventListener('abort', abort, { once: true });
    }
    xhr.send(file);
  });
}

export type { Attachment };
